import React, { useState } from "react";
import { Loader2, Check, AlertCircle, Sparkles, X } from "lucide-react";

/**
 * DemoCatalogSeeder
 * ------------------------------------------------------------------
 * One-click loader for the Vegas Market demo catalog: 9 products,
 * 3 categories, 3 price tiers each.
 *
 * It deliberately uses the SAME props the manual uploader already uses
 * (onUploadImage → onCreate → onUpdate), so nothing here is a second
 * code path that can drift from the real one. If manual entry works,
 * this works; if this breaks, manual entry was already broken.
 *
 * Image handling, in order of preference:
 *   1. Fetch the Pexels CDN image → File → onUploadImage() → your own
 *      Supabase bucket. This is the path you want: the image ends up on
 *      infrastructure you control, so the demo doesn't depend on a
 *      third-party CDN being reachable from the trade show floor.
 *   2. If that fetch is blocked (CORS) or upload fails, fall back to
 *      hotlinking the Pexels URL directly. Still works — ClickSend can
 *      fetch a public URL for MMS — but you're now depending on their
 *      CDN mid-call.
 *
 * All 9 load as item_kind="product", never "deal". Deals stay empty so
 * the deal-vs-catalog distinction stays clean for the next pass.
 *
 * REMOVE AFTER THE SHOW: delete this file and the <DemoCatalogSeeder />
 * line in CatalogDrawer. Nothing else references it.
 *
 * Photos: Pexels License — free for commercial use, no attribution
 * required. https://www.pexels.com/license/
 */

const px = (id) =>
  `https://images.pexels.com/photos/${id}/pexels-photo-${id}.jpeg?auto=compress&cs=tinysrgb&w=1200`;

export const DEMO_CATALOG = [
  // ---------- SOFAS & SECTIONALS ----------
  {
    slug: "harbor-sofa",
    tier: "Entry",
    title: "Harbor 3-Seat Sofa",
    price: "$599",
    options: "Charcoal, Oatmeal, Slate Blue",
    category: "sofas_sectionals",
    imageUrl: px(8583743),
    description:
      "Compact three-seat sofa, 78 inches wide. Stain-resistant polyester weave over an engineered hardwood frame with sinuous spring support. Reversible seat cushions, removable back pillows. Ships flat and assembles in about fifteen minutes with no tools. Good fit for apartments, first homes, and bonus rooms.",
    spoken:
      "The Harbor three-seat sofa is five ninety-nine, in charcoal, oatmeal, or slate blue.",
  },
  {
    slug: "westbrook-sectional",
    tier: "Mid",
    title: "Westbrook 5-Piece Sectional",
    price: "$1,499",
    options: "Left or right chaise · Pebble, Fog, Espresso",
    category: "sofas_sectionals",
    imageUrl: px(6510967),
    description:
      "Five-piece modular sectional, 112 by 88 inches, with a reversible chaise that sets up on either end. Performance chenille rated for pets and kids over a kiln-dried hardwood frame. High-resiliency foam cushions wrapped in a down blend. Includes two matching accent pillows.",
    spoken:
      "The Westbrook five-piece sectional runs fourteen ninety-nine, and the chaise works on either side.",
  },
  {
    slug: "ardmore-sectional",
    tier: "Premium",
    title: "Ardmore Power Reclining Leather Sectional",
    price: "$3,899",
    options: "Cognac, Black, Walnut · Optional power headrest",
    category: "sofas_sectionals",
    imageUrl: px(6510974),
    description:
      "Top-grain leather sectional with two power recliners, USB charging in both console arms, and hidden storage. Eight-way hand-tied suspension on a kiln-dried hardwood frame. Full aniline leather on all seating surfaces, matched vinyl on the outside back. Seats six comfortably.",
    spoken:
      "The Ardmore power reclining leather sectional is thirty-eight ninety-nine, with USB charging built into both arms.",
  },

  // ---------- DINING ----------
  {
    slug: "alder-dining-set",
    tier: "Entry",
    title: "Alder 5-Piece Dining Set",
    price: "$449",
    options: "Natural or Espresso · Seats 4",
    category: "dining",
    imageUrl: px(3968061),
    description:
      "Five-piece set: a 42-inch square table plus four side chairs. Wood-grain laminate over MDF with a powder-coated steel base and padded fabric seats. Assembles in under an hour. A straightforward starter set for smaller kitchens and dining nooks.",
    spoken: "The Alder five-piece dining set is four forty-nine, and it seats four.",
  },
  {
    slug: "fairmont-dining-table",
    tier: "Mid",
    title: "Fairmont Extendable Dining Table",
    price: "$1,099",
    options: "Seats 6, extends to 8 · Chairs sold separately",
    category: "dining",
    imageUrl: px(6312360),
    description:
      "Solid acacia table with a self-storing butterfly leaf — 68 inches closed, 86 inches extended. Hand-rubbed matte finish that shows the natural grain variation. Tapered solid-wood legs with no center post, so nobody straddles a table leg. Chairs sold separately.",
    spoken:
      "The Fairmont extendable dining table is ten ninety-nine, and it opens from six seats to eight.",
  },
  {
    slug: "stonecrest-dining-table",
    tier: "Premium",
    title: "Stonecrest Live-Edge Dining Table",
    price: "$2,750",
    options: '86" or 96" · Blackened steel or brass base',
    category: "dining",
    imageUrl: px(3968056),
    description:
      "Single-slab black walnut top with a natural live edge, finished in a hand-applied hardwax oil that can be spot-repaired rather than fully refinished. Cast steel trestle base in blackened iron or brushed brass. Every top has different figuring, so no two tables are identical. Seats eight to ten.",
    spoken:
      "The Stonecrest live-edge walnut table is twenty-seven fifty, and every top has its own grain pattern.",
  },

  // ---------- MATTRESSES ----------
  {
    slug: "restwell-memory-foam",
    tier: "Entry",
    title: 'RestWell 8" Memory Foam, Queen',
    price: "$349",
    options: "Twin, Full, Queen, King · Medium-firm",
    category: "mattresses",
    imageUrl: px(212269),
    description:
      "Eight-inch CertiPUR-US certified memory foam over a high-density support base. Medium-firm feel. Ships compressed in a box and expands fully in about 24 hours. Ten-year limited warranty. A solid guest room or first apartment mattress.",
    spoken:
      "The RestWell eight-inch memory foam queen is three forty-nine, and it ships in a box.",
  },
  {
    slug: "cascade-hybrid",
    tier: "Mid",
    title: 'Cascade Hybrid 12", Queen',
    price: "$899",
    options: "Twin through Cal King · Plush or Firm",
    category: "mattresses",
    imageUrl: px(376531),
    description:
      "Twelve-inch hybrid: individually pocketed coils for support and motion isolation, topped with gel-infused memory foam for heat control. Reinforced perimeter so you can sit on the edge without roll-off. Available plush or firm. The everyday recommendation for couples who sleep differently.",
    spoken:
      "The Cascade twelve-inch hybrid queen is eight ninety-nine, and it comes in plush or firm.",
  },
  {
    slug: "sovereign-latex-hybrid",
    tier: "Premium",
    title: 'Sovereign Latex Hybrid 14", Queen',
    price: "$2,199",
    options: "Queen, King, Cal King · Optional adjustable base",
    category: "mattresses",
    imageUrl: px(7598137),
    description:
      "Fourteen-inch build with natural Talalay latex over zoned pocketed coils — firmer under the hips, softer at the shoulders. Organic cotton and wool cover with no chemical flame retardants. Latex sleeps cooler than memory foam and holds its shape for years. Twenty-five-year warranty. Pairs with an adjustable base.",
    spoken:
      "The Sovereign latex hybrid queen is twenty-one ninety-nine, with zoned coils and a natural latex top.",
  },
];

/**
 * Pull the remote image into a File so it can go through the store's own
 * upload path. Returns null if that isn't possible, so the caller can
 * decide whether to hotlink instead.
 */
async function toUploadableFile(item) {
  const res = await fetch(item.imageUrl, { mode: "cors" });
  if (!res.ok) throw new Error(`Image fetch failed (${res.status})`);
  const blob = await res.blob();
  if (!blob || blob.size === 0) throw new Error("Image fetch returned nothing");
  return new File([blob], `${item.slug}.jpg`, {
    type: blob.type || "image/jpeg",
  });
}

const DemoCatalogSeeder = ({
  onUploadImage,
  onCreate,
  onUpdate,
  existingCount = 0,
}) => {
  const [dismissed, setDismissed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [doneCount, setDoneCount] = useState(0);
  const [currentLabel, setCurrentLabel] = useState("");
  const [finished, setFinished] = useState(false);
  const [errors, setErrors] = useState([]);
  const [hotlinked, setHotlinked] = useState(0);

  if (dismissed || !onCreate) return null;

  const run = async () => {
    if (
      existingCount > 0 &&
      !window.confirm(
        `You already have ${existingCount} item${existingCount === 1 ? "" : "s"} here. ` +
          "Load the 9 demo products on top of them?"
      )
    ) {
      return;
    }

    setBusy(true);
    setErrors([]);
    setDoneCount(0);
    setHotlinked(0);
    setFinished(false);

    const failures = [];
    let hotlinkCount = 0;

    for (let i = 0; i < DEMO_CATALOG.length; i += 1) {
      const item = DEMO_CATALOG[i];
      setCurrentLabel(item.title);
      try {
        // 1. Get the image onto your own storage if we can.
        let thumbnailUrl = item.imageUrl;
        if (onUploadImage) {
          try {
            const file = await toUploadableFile(item);
            const uploaded = await onUploadImage(file);
            if (uploaded) {
              thumbnailUrl = uploaded;
            } else {
              hotlinkCount += 1;
            }
          } catch {
            // CORS or upload failure — hotlink instead, don't lose the item.
            hotlinkCount += 1;
          }
        } else {
          hotlinkCount += 1;
        }

        // 2. Create through the exact same call the manual form makes.
        const created = await onCreate({
          thumbnail_url: thumbnailUrl,
          description: item.description,
          title: item.title,
          kind: "product",
          price: item.price,
          options: item.options,
        });

        // 3. Category + spoken line aren't part of the create payload, so
        //    patch them in the way the inline card editor does. Skipped
        //    silently if the parent didn't wire up onUpdate.
        if (onUpdate && created?.id) {
          try {
            await onUpdate(created.id, {
              deal_category: item.category,
              deal_spoken: item.spoken,
            });
          } catch {
            failures.push(`${item.title}: saved, but category/spoken didn't stick`);
          }
        }

        setDoneCount(i + 1);
      } catch (err) {
        failures.push(`${item.title}: ${err?.message || "failed"}`);
      }
    }

    setErrors(failures);
    setHotlinked(hotlinkCount);
    setCurrentLabel("");
    setBusy(false);
    setFinished(true);
  };

  const total = DEMO_CATALOG.length;

  return (
    <div className="mb-4 rounded-xl border border-amber-200 bg-gradient-to-br from-amber-50 to-white p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-2.5 min-w-0">
          <div className="w-8 h-8 rounded-lg bg-amber-100 text-amber-700 flex items-center justify-center flex-shrink-0">
            <Sparkles className="w-4 h-4" />
          </div>
          <div className="min-w-0">
            <h3 className="text-[13px] font-bold text-slate-900">
              Demo catalog
            </h3>
            <p className="text-[12px] text-slate-600 leading-relaxed mt-0.5">
              Loads 9 sample products — sofas, dining, and mattresses, three
              price points each — with descriptions, prices, categories, and
              spoken lines already written.
            </p>
          </div>
        </div>
        {!busy && (
          <button
            type="button"
            onClick={() => setDismissed(true)}
            className="p-1 text-slate-400 hover:text-slate-700 hover:bg-white rounded transition-all flex-shrink-0"
            aria-label="Dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      <div className="mt-3 flex items-center gap-3 flex-wrap">
        <button
          type="button"
          onClick={run}
          data-tip="Adds the 9 sample products to this catalog alongside any existing items"
          disabled={busy}
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-amber-600 hover:bg-amber-700 disabled:bg-amber-300 text-white text-[12px] font-semibold transition-all"
        >
          {busy ? (
            <>
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
              Adding {doneCount + 1} of {total}…
            </>
          ) : finished ? (
            "Load again"
          ) : (
            `Load ${total} demo products`
          )}
        </button>

        {busy && currentLabel && (
          <span className="text-[11px] text-slate-500 truncate">
            {currentLabel}
          </span>
        )}

        {finished && !busy && errors.length === 0 && (
          <span className="inline-flex items-center gap-1 text-[12px] font-semibold text-emerald-700">
            <Check className="w-3.5 h-3.5" />
            {doneCount} of {total} added
          </span>
        )}
      </div>

      {busy && (
        <div className="mt-2.5 h-1 w-full rounded-full bg-amber-100 overflow-hidden">
          <div
            className="h-full bg-amber-500 transition-all duration-300"
            style={{ width: `${(doneCount / total) * 100}%` }}
          />
        </div>
      )}

      {finished && hotlinked > 0 && (
        <p className="mt-2.5 text-[11px] text-amber-800 leading-relaxed">
          {hotlinked} image{hotlinked === 1 ? "" : "s"} couldn't be copied into
          your own storage and {hotlinked === 1 ? "is" : "are"} linked from
          Pexels instead. That still sends over MMS, but it depends on their CDN
          — worth re-uploading those manually before the floor opens.
        </p>
      )}

      {finished && errors.length > 0 && (
        <div className="mt-2.5 rounded-lg border border-red-200 bg-red-50 p-2.5">
          <div className="flex items-center gap-1.5 mb-1">
            <AlertCircle className="w-3.5 h-3.5 text-red-600 flex-shrink-0" />
            <span className="text-[12px] font-semibold text-red-800">
              {errors.length} item{errors.length === 1 ? "" : "s"} had trouble
            </span>
          </div>
          <ul className="text-[11px] text-red-700 space-y-0.5 list-disc pl-4">
            {errors.map((e, i) => (
              <li key={i}>{e}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default DemoCatalogSeeder;